import { useFacilityModeStore } from '@/entities/facility/model/facility-mode.store';
import { getRbacRoute, useSessionStore } from '@/entities/session';
import {
  GlobalErrorBoundary,
  GlobalPageLoader,
  NotFoundPage,
} from '@/shared/components';
import { PROTECTED_ROUTE } from '@/shared/constants/sidebar-config';
import { MarketIntelTabId } from '@/widgets/market-intel-tabs/model/types';
import type {
  IntegrationSubTabId,
  LogsSubTabId,
  SettingTabId,
} from '@/widgets/setting-tabs/model/types';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  RouterProvider as TanstackRouterProvider,
} from '@tanstack/react-router';
import type { FC } from 'react';
import { Layout } from '../layouts';

/* LAZY ROUTES */
const lazyLoginRoute = () =>
  import('@/pages/login/login.route').then(({ Route }) => Route);
const lazyForgotPasswordRoute = () =>
  import('@/pages/forgot-password/forgot-password.route').then(
    ({ Route }) => Route
  );
const lazyFacilityEditRoute = () =>
  import('@/pages/facility-edit/facility-edit.route').then(
    ({ Route }) => Route
  );

const lazyResetPasswordRoute = () =>
  import('@/pages/reset-password/reset-password.route').then(
    ({ Route }) => Route
  );
const lazyPasswordResetLinkRoute = () =>
  import('@/pages/password-reset-link/password-reset-link.route').then(
    ({ Route }) => Route
  );
const lazyFacilitiesRoute = () =>
  import('@/pages/facilities/facilities.route').then(({ Route }) => Route);
const lazyMarketFacilitiesRoute = () =>
  import('@/pages/market-facilities/market-facilities.route').then(
    ({ Route }) => Route
  );
const lazyMarketFacilitiesFormRoute = () =>
  import('@/pages/market-facilities-form/market-facilities-form.route').then(
    ({ Route }) => Route
  );
const lazyMarketFacilitiesFormEditRoute = () =>
  import(
    '@/pages/market-facilities-form-edit/market-facilities-form-edit.route'
  ).then(({ Route }) => Route);
const lazyAlertsRoute = () =>
  import('@/pages/alerts/alerts.route').then(({ Route }) => Route);
const lazyAlertsSingleRoute = () =>
  import('@/pages/alerts-single/alerts-single.route').then(
    ({ Route }) => Route
  );
const lazyResidentsRoute = () =>
  import('@/pages/residents/residents.route').then(({ Route }) => Route);
const lazyResidentSingleRoute = () =>
  import('@/pages/resident-single/resident-single.route').then(
    ({ Route }) => Route
  );
const lazyMarketIntelRoute = () =>
  import('@/pages/market-intel/market-intel.route').then(({ Route }) => Route);
const lazyStaffRoute = () =>
  import('@/pages/staff/staff.route').then(({ Route }) => Route);
const lazySettingsRoute = () =>
  import('@/pages/settings/settings.route').then(({ Route }) => Route);
const lazyInviteAdminRoute = () =>
  import('@/pages/invite-admin/invite-admin.route').then(({ Route }) => Route);
const lazyInviteRegularUserRoute = () =>
  import('@/pages/invite-regular-user/invite-regular-user.route').then(
    ({ Route }) => Route
  );
const lazyUserRoute = () =>
  import('@/pages/user/user.route').then(({ Route }) => Route);
const lazyStaffSingleRoute = () =>
  import('@/pages/staff-single/staff-single.route').then(({ Route }) => Route);
const lazyExportPDFRoute = () =>
  import('@/pages/export-pdf/export-pdf.route').then(({ Route }) => Route);
const lazyLoginActivityRoute = () =>
  import('@/pages/login-activity/login-activity.route').then(
    ({ Route }) => Route
  );
const lazyStaffUsageRoute = () =>
  import('@/pages/staff-usage/staff-usage.route').then(({ Route }) => Route);
const lazyChangelogRoute = () =>
  import('@/pages/changelog/changelog.route').then(({ Route }) => Route);
const lazyImpersonateRoute = () =>
  import('@/pages/impersonate/impersonate.route').then(({ Route }) => Route);

/* BASIC ROUTES SETUP */
const rootRoute = createRootRoute({
  beforeLoad: async () => {
    const isAuthenticated = useSessionStore.getState().isAuthenticated;
    if (isAuthenticated === null) {
      await useSessionStore.getState().checkSession();
    }
  },
  component: () => (
    <>
      <Outlet />
      {/*<TanStackRouterDevtools /> */}
    </>
  ),
});
const indexRoute = createRoute({
  path: '/',
  getParentRoute: () => rootRoute,
  beforeLoad: async () => {
    const isAuthenticated = useSessionStore.getState().isAuthenticated;
    if (isAuthenticated) {
      const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.FACILITIES);
      if (!allowed) {
        throw redirect({ to: nextRoute });
      }
      throw redirect({ to: '/facilities' });
    }
    throw redirect({ to: '/login' });
  },
});
const publicRoute = createRoute({
  id: 'public',
  getParentRoute: () => rootRoute,
  beforeLoad: async () => {
    const isAuthenticated = useSessionStore.getState().isAuthenticated;
    if (isAuthenticated === true) {
      const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.FACILITIES);
      if (!allowed) {
        throw redirect({ to: nextRoute });
      }
      throw redirect({ to: '/facilities' });
    }
  },
  component: () => (
    <Layout.Public>
      <Outlet />
    </Layout.Public>
  ),
});

const protectedRoute = createRoute({
  id: 'protected',
  getParentRoute: () => rootRoute,
  beforeLoad: async () => {
    const isAuthenticated = useSessionStore.getState().isAuthenticated;
    if (!isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: () => {
    return (
      <Layout.Platform>
        <Outlet />
      </Layout.Platform>
    );
  },
});

const hiddenRoute = createRoute({
  id: 'export',
  getParentRoute: () => rootRoute,
  beforeLoad: async () => {
    const isAuthenticated = useSessionStore.getState().isAuthenticated;
    if (!isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: () => {
    return (
      <Layout.Hidden>
        <Outlet />
      </Layout.Hidden>
    );
  },
});

/* PUBLIC ROUTES */
const loginRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/login',
}).lazy(lazyLoginRoute);

const forgotPasswordRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/forgot-password',
}).lazy(lazyForgotPasswordRoute);

const resetPasswordRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/reset-password',
  validateSearch: (search: { token: string }) => {
    if (!search.token) {
      throw new Error('Token is required');
    }
    return search;
  },
}).lazy(lazyResetPasswordRoute);

const passwordResetLinkRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/password-reset-link',
}).lazy(lazyPasswordResetLinkRoute);

const impersonateRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/impersonate',
  validateSearch: (search: { token: string }) => {
    if (!search.token) {
      throw new Error('Token is required');
    }
    return search;
  },
}).lazy(lazyImpersonateRoute);

/* PROTECTED ROUTES */
const alertsRoute = createRoute({
  path: PROTECTED_ROUTE.ALERTS,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.ALERTS);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyAlertsRoute);

const alertsSingleRoute = createRoute({
  path: PROTECTED_ROUTE.ALERTS_SINGLE,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.ALERTS_SINGLE);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyAlertsSingleRoute);

const facilitiesRoute = createRoute({
  path: PROTECTED_ROUTE.FACILITIES,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.FACILITIES);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyFacilitiesRoute);

const marketFacilitiesRoute = createRoute({
  path: PROTECTED_ROUTE.MARKET_FACILITIES,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(
      PROTECTED_ROUTE.MARKET_FACILITIES
    );
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyMarketFacilitiesRoute);

const marketFacilitiesFormRoute = createRoute({
  path: PROTECTED_ROUTE.MARKET_FACILITIES_FORM_NEW,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(
      PROTECTED_ROUTE.MARKET_FACILITIES
    );
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyMarketFacilitiesFormRoute);

const marketFacilitiesFormEditRoute = createRoute({
  path: PROTECTED_ROUTE.MARKET_FACILITIES_FORM_EDIT,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(
      PROTECTED_ROUTE.MARKET_FACILITIES
    );
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyMarketFacilitiesFormEditRoute);

/**
 * The standalone Single Facility page was replaced entirely (card #157):
 * selecting a facility now filters every dashboard instead of opening its own
 * page. The route is kept only as a redirect so existing bookmarks and links
 * still land somewhere sensible -- it selects the facility and drops the user
 * on the unified Facilities page.
 */
const facilityRoute = createRoute({
  path: PROTECTED_ROUTE.FACILITY,
  getParentRoute: () => protectedRoute,
  beforeLoad: ({ params }) => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.FACILITY);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }

    const { facilityId } = params as { facilityId?: string };

    // useFacilityMode drops an id the user cannot access, so a stale or
    // forbidden bookmark falls back to Portfolio Mode rather than stranding
    // them in Single Mode on a facility they can no longer see.
    if (facilityId) {
      useFacilityModeStore.getState().requestFacility(facilityId);
    }

    throw redirect({ to: '/facilities' });
  },
});

const facilityEditRoute = createRoute({
  path: PROTECTED_ROUTE.FACILITY_EDIT,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.FACILITY_EDIT);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyFacilityEditRoute);

const staffRoute = createRoute({
  path: PROTECTED_ROUTE.STAFF,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.STAFF);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyStaffRoute);

const residentsRoute = createRoute({
  path: PROTECTED_ROUTE.RESIDENTS,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.RESIDENTS);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyResidentsRoute);

const residentSingleRoute = createRoute({
  path: PROTECTED_ROUTE.RESIDENT_SINGLE,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(
      PROTECTED_ROUTE.RESIDENT_SINGLE
    );
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyResidentSingleRoute);

const marketIntelRoute = createRoute({
  path: PROTECTED_ROUTE.MARKET_INTEL,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.MARKET_INTEL);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
  // The sidebar links to a bare `/market-intel`, so re-entering the page while
  // already on it would otherwise strip `tab` and leave every panel unrendered.
  // Defaulting here covers that plus bookmarks and hand-typed URLs.
  validateSearch: (search: { tab?: MarketIntelTabId }) => ({
    tab: search.tab ?? MarketIntelTabId.General,
  }),
}).lazy(lazyMarketIntelRoute);

const settingsRoute = createRoute({
  path: PROTECTED_ROUTE.SETTINGS,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.SETTINGS);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
  validateSearch: (search: {
    tab?: SettingTabId;
    logsTab?: LogsSubTabId;
    integrationTab?: IntegrationSubTabId;
  }) => search,
}).lazy(lazySettingsRoute);

const inviteAdminRoute = createRoute({
  path: PROTECTED_ROUTE.INVITE_ADMIN,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.INVITE_ADMIN);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyInviteAdminRoute);

const inviteRegularUserRoute = createRoute({
  path: PROTECTED_ROUTE.INVITE_REGULAR_USER,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(
      PROTECTED_ROUTE.INVITE_REGULAR_USER
    );
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyInviteRegularUserRoute);

const staffSingleRoute = createRoute({
  path: PROTECTED_ROUTE.STAFF_SINGLE,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.STAFF_SINGLE);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyStaffSingleRoute);

const singleUserRoute = createRoute({
  path: '/user/$userId',
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.USER);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }

    // TODO: usequery(getuserqueryoptions)
  },
}).lazy(lazyUserRoute);

const loginActivityRoute = createRoute({
  path: PROTECTED_ROUTE.LOGIN_ACTIVITY,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.LOGIN_ACTIVITY);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyLoginActivityRoute);

const staffUsageRoute = createRoute({
  path: PROTECTED_ROUTE.STAFF_USAGE,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.STAFF_USAGE);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyStaffUsageRoute);

const changelogRoute = createRoute({
  path: PROTECTED_ROUTE.CHANGELOG,
  getParentRoute: () => protectedRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.CHANGELOG);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
}).lazy(lazyChangelogRoute);

const exportPDFRoute = createRoute({
  path: PROTECTED_ROUTE.EXPORT_PDF,
  getParentRoute: () => hiddenRoute,
  beforeLoad: () => {
    const { allowed, nextRoute } = getRbacRoute(PROTECTED_ROUTE.EXPORT_PDF);
    if (!allowed) {
      throw redirect({ to: nextRoute });
    }
  },
  validateSearch: (search: {
    facilityId: number;
    set: 'INTERNAL' | 'COMP';
    from: string;
    to: string;
  }) => search,
}).lazy(lazyExportPDFRoute);

/* CONFIGS */
const routeTree = rootRoute.addChildren([
  indexRoute,
  publicRoute.addChildren([
    loginRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    passwordResetLinkRoute,
    impersonateRoute,
  ]),
  protectedRoute.addChildren([
    alertsRoute,
    alertsSingleRoute,
    facilitiesRoute,
    marketFacilitiesRoute,
    marketFacilitiesFormRoute,
    marketFacilitiesFormEditRoute,
    staffRoute,
    staffSingleRoute,
    residentsRoute,
    residentSingleRoute,
    marketIntelRoute,
    facilityRoute,
    facilityEditRoute,
    settingsRoute,
    inviteAdminRoute,
    inviteRegularUserRoute,
    singleUserRoute,
    loginActivityRoute,
    staffUsageRoute,
    changelogRoute,
  ]),
  hiddenRoute.addChildren([exportPDFRoute]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestorationBehavior: 'smooth',
  scrollRestoration: true,
  defaultPendingComponent: () => <GlobalPageLoader />,
  defaultErrorComponent: (error) => <GlobalErrorBoundary {...error} />,
  defaultNotFoundComponent: () => (
    <Layout.Public>
      <NotFoundPage />
    </Layout.Public>
  ),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/* PROVIDER */
export const RouterProvider: FC = () => {
  return <TanstackRouterProvider router={router} />;
};
